/**
 * Small shared pieces ported from the mockup (Stage/Eyebrow/H1/Sub/Card/
 * Metric/LegendSwatch/OutcomeCard/SavingsBar/ChecklistCard/AsteriskNote).
 * All colors come from theme tokens — no brand hex literals here.
 */
import React from 'react';
import { Check } from 'lucide-react';
import { T, FONT, SERIF } from './theme';

export const Stage: React.FC<{ children: React.ReactNode; dark?: boolean }> = ({ children, dark }) => (
  <div style={{ height: '100%', overflowY: 'auto', background: dark ? T.navy : T.bg }}>
    <div style={{ maxWidth: 1080, margin: '0 auto', padding: '46px 40px 60px' }}>{children}</div>
  </div>
);

export const Eyebrow: React.FC<{ children: React.ReactNode; light?: boolean }> = ({ children, light }) => (
  <div
    style={{
      fontFamily: FONT,
      fontSize: 11.5,
      fontWeight: 700,
      letterSpacing: 1.5,
      textTransform: 'uppercase',
      color: light ? T.teal : T.gray,
      marginBottom: 10,
    }}
  >
    {children}
  </div>
);

export const H1: React.FC<{ children: React.ReactNode; light?: boolean }> = ({ children, light }) => (
  <h1
    style={{
      fontFamily: SERIF,
      fontSize: 38,
      fontWeight: 600,
      letterSpacing: '-0.01em',
      textTransform: 'uppercase',
      color: light ? '#fff' : T.navy,
      margin: '0 0 8px',
      lineHeight: 1.05,
    }}
  >
    {children}
  </h1>
);

export const Sub: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{ fontFamily: FONT, fontSize: 15.5, color: T.gray, margin: '0 0 30px', lineHeight: 1.5 }}>
    {children}
  </p>
);

export const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({
  children,
  style,
}) => (
  <div
    // deck-card: the print page (PrintReportView) marks these break-inside:avoid
    // so a card is never sliced across PDF pages. Unstyled outside print.
    className="deck-card"
    style={{
      background: T.card,
      borderRadius: 12,
      boxShadow: '0 2px 10px rgba(27,42,74,.07)',
      padding: 24,
      ...style,
    }}
  >
    {children}
  </div>
);

export const Metric: React.FC<{ value: string; label: string; note?: string | null; small?: boolean; accent?: boolean }> = ({
  value,
  label,
  note,
  small,
  accent,
}) => (
  <div
    style={{
      background: accent ? T.tealSoft : '#F6F8FA',
      borderRadius: 9,
      padding: small ? '11px 14px' : '16px 18px',
    }}
  >
    <div style={{ fontSize: small ? 21 : 27, fontWeight: 700, color: accent ? T.teal : T.navy }}>{value}</div>
    <div style={{ fontSize: 12, color: T.gray }}>{label}</div>
    {note && <div style={{ fontSize: 11.5, color: T.gray, marginTop: 2 }}>{note}</div>}
  </div>
);

/** The mockup's `Legend` — swatch + label row item. Color must be concrete inside SVG-free HTML rows, so any CSS value works. */
export const LegendSwatch: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <span style={{ width: 11, height: 11, borderRadius: 3, background: color }} />
    {label}
  </span>
);

export const OutcomeCard: React.FC<{
  icon?: React.ReactNode;
  title: string;
  value: string;
  sub?: string | null;
  highlight?: boolean;
}> = ({ icon, title, value, sub, highlight }) => (
  <div
    className="deck-card"
    style={{
      background: highlight ? T.teal : T.card,
      border: highlight ? 'none' : `1px solid ${T.grayLine}`,
      boxShadow: highlight ? '0 4px 14px rgba(27,42,74,.12)' : '0 2px 8px rgba(27,42,74,.05)',
      borderRadius: 12,
      padding: '16px 18px',
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        color: highlight ? 'rgba(255,255,255,.92)' : T.teal,
        fontSize: 13.5,
        fontWeight: 600,
        marginBottom: highlight ? 8 : 4,
      }}
    >
      {icon}
      {title}
    </div>
    <div style={{ fontSize: highlight ? 30 : 19, fontWeight: 700, color: highlight ? '#fff' : T.navy, lineHeight: 1.1 }}>
      {value}
    </div>
    {sub && (
      <div style={{ fontSize: 12.5, color: highlight ? 'rgba(255,255,255,.85)' : T.gray, marginTop: 2 }}>{sub}</div>
    )}
  </div>
);

export const SavingsBar: React.FC<{ label: string; amt: string; pct: number; color: string }> = ({
  label,
  amt,
  pct,
  color,
}) => (
  <div style={{ marginBottom: 14, borderLeft: `3px solid ${color}`, paddingLeft: 12 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <div>
        <div style={{ fontSize: 13, color: T.gray }}>{label}</div>
        <div style={{ fontSize: 17, fontWeight: 700, color: T.navy }}>{amt}</div>
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{pct}%</div>
    </div>
  </div>
);

export const ChecklistCard: React.FC<{ title: string; items: string[] }> = ({ title, items }) => (
  <Card>
    <Eyebrow>{title}</Eyebrow>
    {items.map((it) => (
      <div key={it} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0' }}>
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: 11,
            background: T.teal,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Check size={14} color="#fff" />
        </div>
        <span style={{ fontSize: 14, color: T.navy, fontWeight: 500 }}>{it}</span>
      </div>
    ))}
  </Card>
);

/** Asterisk + gray disclaimer row used beneath estimated values and charts. */
export const AsteriskNote: React.FC<{ text: string }> = ({ text }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 8,
      marginTop: 12,
      fontSize: 11.5,
      color: T.gray,
      lineHeight: 1.5,
    }}
  >
    <span style={{ color: T.teal, fontWeight: 700, fontSize: 13, lineHeight: 1 }}>*</span>
    <span>{text}</span>
  </div>
);

/** Recharts Tooltip contentStyle — HTML element, so neutral values only. */
export const ttStyle: React.CSSProperties = {
  borderRadius: 8,
  border: '1px solid rgba(27,42,74,.14)',
  fontSize: 12,
  fontFamily: FONT,
  boxShadow: '0 4px 12px rgba(27,42,74,.1)',
};
